import {
  Box,
  Typography,
} from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AirIcon from '@mui/icons-material/Air';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { HDate } from '@hebcal/core';
import { heritageColors } from '../../../../heritageTheme.ts';
import { computeSeasonalPrayers } from '../../../../engine/halacha/seasonalPrayers';

const Card = ({ weather, loading, error, title = 'מזג אוויר', showTitle = false }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: heritageColors.slate }}>
        טוען נתונים...
      </Box>
    );
  }

  const getMuiWeatherIcon = (iconCode) => {
    const iconMap = {
      '01d': <WbSunnyIcon sx={{ fontSize: 40, color: heritageColors.gold }} />,
      '01n': <NightsStayIcon sx={{ fontSize: 40, color: heritageColors.navy }} />,
      '02d': <WbSunnyIcon sx={{ fontSize: 40, color: heritageColors.gold }} />,
      '02n': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
      '03d': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
      '03n': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
      '04d': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
      '04n': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
      '09d': <WaterDropIcon sx={{ fontSize: 40, color: heritageColors.teal }} />,
      '09n': <WaterDropIcon sx={{ fontSize: 40, color: heritageColors.teal }} />,
      '10d': <WaterDropIcon sx={{ fontSize: 40, color: heritageColors.teal }} />,
      '10n': <WaterDropIcon sx={{ fontSize: 40, color: heritageColors.teal }} />,
      '11d': <ThunderstormIcon sx={{ fontSize: 40, color: heritageColors.navy }} />,
      '11n': <ThunderstormIcon sx={{ fontSize: 40, color: heritageColors.navy }} />,
      '13d': <AcUnitIcon sx={{ fontSize: 40, color: heritageColors.teal }} />,
      '13n': <AcUnitIcon sx={{ fontSize: 40, color: heritageColors.teal }} />,
      '50d': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
      '50n': <CloudIcon sx={{ fontSize: 40, color: heritageColors.slate }} />,
    };
    return iconMap[iconCode] || <WbSunnyIcon sx={{ fontSize: 40, color: heritageColors.gold }} />;
  };

  const now = new Date();
  const hdate = new HDate(now);
  const { mashivHaruach } = computeSeasonalPrayers(hdate, true, 'sephardic', now);
  const halachicStatus = mashivHaruach ? '"משיב הרוח ומוריד הגשם"' : '"מוריד הטל"';

  return (
    <Box sx={{ p: 3, direction: 'rtl' }}>
      {/* Title */}
      {showTitle && (
        <Typography sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: heritageColors.navy,
          mb: 2,
          textAlign: 'center'
        }}>
          {title}
        </Typography>
      )}

      {/* Main temperature display */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography sx={{
            fontSize: '3.5rem',
            fontWeight: 700,
            color: heritageColors.navy,
            lineHeight: 1,
          }}>
            {Math.round(weather?.main?.temp || 24)}°
          </Typography>
          <Typography sx={{ color: heritageColors.slate, mt: 0.5 }}>
            {weather?.name || 'ירושלים'}
          </Typography>
        </Box>
        <Box sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: heritageColors.goldLight + '20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {getMuiWeatherIcon(weather?.weather?.[0]?.icon)}
        </Box>
      </Box>

      {/* Weather details */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WaterDropIcon sx={{ fontSize: 16, color: heritageColors.teal }} />
          <Typography sx={{ fontSize: '0.85rem', color: heritageColors.slate }}>
            לחות: {weather?.main?.humidity || 45}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AirIcon sx={{ fontSize: 16, color: heritageColors.slate }} />
          <Typography sx={{ fontSize: '0.85rem', color: heritageColors.slate }}>
            רוח: {Math.round((weather?.wind?.speed || 4.2) * 3.6)} קמ"ש
          </Typography>
        </Box>
      </Box>

      {/* Prayer description */}
      <Box
        sx={{
          bgcolor: heritageColors.gold,
          color: heritageColors.white,
          py: 1.5,
          borderRadius: 3,
          fontWeight: 600,
          fontSize: '1rem',
          textAlign: 'center',
        }}
      >
        {halachicStatus}
      </Box>

      {error && weather && (
        <Typography sx={{
          color: heritageColors.gold,
          fontSize: '0.75rem',
          mt: 1,
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          ⚠️ מוצג מידע אחרון שנשמר
        </Typography>
      )}
      {error && !weather && (
        <Typography sx={{ color: 'red', fontSize: '0.75rem', mt: 1, textAlign: 'center' }}>
          שגיאה בטעינת נתונים
        </Typography>
      )}
    </Box>
  );
};

export default Card;
